// pages/reporteria/ventas/VentasTalleres.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Button, 
  Space, 
  Spin,
  Alert,
  Select,
  Badge,
  Typography,
  Divider,
  Progress,
  Empty,
  message,
  Tooltip,
  Tag,
  Table,
  Tabs,
  Modal,
  List,
  Avatar,
  Checkbox
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
  PieChartOutlined,
  BarChartOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FilterOutlined,
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  StarOutlined,
  TeamOutlined,
  PercentageOutlined,
  EyeOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReporteriaService from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../utils/exportExcel';
import { Line, Bar, Pie, Doughnut, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler
} from 'chart.js';
import { LuGitCompare } from 'react-icons/lu';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const { RangePicker } = DatePicker;
const { Title: AntTitle, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const VentasTalleres = () => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    
    // Estados para datos
    const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
    const [topTalleres, setTopTalleres] = useState(null);
    const [matrizRentabilidad, setMatrizRentabilidad] = useState(null);
    const [evolucion, setEvolucion] = useState(null);
    
    // Modal de comparación
    const [modalComparar, setModalComparar] = useState(false);
    const [talleresSeleccionados, setTalleresSeleccionados] = useState([]);
    const [comparacion, setComparacion] = useState(null);
    
    // Filtros
    const [fechas, setFechas] = useState([
        dayjs().subtract(3, 'month'),
        dayjs()
    ]);
    const [criterioTop, setCriterioTop] = useState('ingresos');
    const [limiteTop, setLimiteTop] = useState(10);
    const [mesesEvolucion, setMesesEvolucion] = useState(6);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const params = {
                fechaInicio: fechas[0].format('YYYY-MM-DD'),
                fechaFin: fechas[1].format('YYYY-MM-DD')
            };

            const [generales, top, matriz, evol] = await Promise.all([
                ReporteriaService.GetEstadisticasVentas(params),
                ReporteriaService.GetTopTalleres({
                    ...params,
                    limite: limiteTop,
                    ordenarPor: criterioTop
                }),
                ReporteriaService.GetMatrizRentabilidad(params),
                ReporteriaService.GetEvolucionVentas({ meses: mesesEvolucion })
            ]);

            setEstadisticasGenerales(generales);
            setTopTalleres(top);
            setMatrizRentabilidad(matriz);
            setEvolucion(evol);

            message.success('Datos cargados exitosamente');
        } catch (error) {
            console.error('Error al cargar datos:', error);
            message.error('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    const handleComparar = async () => {
        if (talleresSeleccionados.length < 2) {
            message.warning('Selecciona al menos 2 talleres para comparar');
            return;
        }

        try {
            setLoading(true);
            const resultado = await ReporteriaService.CompararTalleres(
                talleresSeleccionados,
                {
                    fechaInicio: fechas[0].format('YYYY-MM-DD'),
                    fechaFin: fechas[1].format('YYYY-MM-DD')
                }
            );
            setComparacion(resultado);
            message.success('Comparación realizada');
        } catch (error) {
            message.error('Error al comparar talleres');
        } finally {
            setLoading(false);
        }
    };

    const formatearMoneda = (valor) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(valor);
    };

    const handleExportarExcel = async () => {
        const top = topTalleres?.topTalleres;
        const generales = estadisticasGenerales;
        if (!top?.length && !generales) {
            message.warning('No hay datos cargados para exportar');
            return;
        }
        try {
            const hojas = [];

            if (generales?.resumen) {
                const r = generales.resumen;
                hojas.push({
                    nombre: 'Resumen',
                    metricas: [
                        { metrica: 'Ingresos' },
                        { metrica: 'Ingresos brutos', valor: r.ingresosBrutos ?? 0, tipo: 'moneda' },
                        { metrica: 'Ingresos totales (neto)', valor: r.ingresosTotal ?? 0, tipo: 'moneda' },
                        { metrica: 'Ticket promedio', valor: r.ticketPromedio ?? 0, tipo: 'moneda' },
                        { metrica: 'Demanda' },
                        { metrica: 'Total de inscripciones', valor: r.totalInscripciones ?? 0, tipo: 'numero' },
                        { metrica: 'Usuarios únicos', valor: r.usuariosUnicos ?? 0, tipo: 'numero' },
                        { metrica: 'Oferta' },
                        { metrica: 'Talleres activos', valor: r.talleresActivos ?? 0, tipo: 'numero' },
                        { metrica: 'Talleres con ventas', valor: r.talleresConVentas ?? 0, tipo: 'numero' },
                        { metrica: 'Ocupación global', valor: r.ocupacionGlobal ?? 0, tipo: 'porcentaje' },
                    ],
                });
            }

            if (top?.length) {
                hojas.push({
                    nombre: 'Top talleres',
                    columnas: [
                        { header: '#', key: 'posicion', tipo: 'numero', ancho: 6 },
                        { header: 'Taller', key: 'nombre', tipo: 'texto', ancho: 38 },
                        { header: 'Categoría', key: 'categoria', tipo: 'texto', ancho: 22 },
                        { header: 'Recinto', key: 'recinto', tipo: 'texto', ancho: 26 },
                        { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
                        { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
                        {
                            header: '% ocupación', key: 'ocupacion', tipo: 'porcentaje',
                            resaltar: (t) => (t.ocupacion >= 80 ? 'ok' : t.ocupacion >= 50 ? 'aviso' : 'alerta'),
                        },
                        { header: 'Ticket promedio', key: 'ticket', tipo: 'moneda' },
                    ],
                    filas: top.map((t, i) => ({
                        posicion: i + 1,
                        nombre: t.nombre,
                        categoria: t.categoria,
                        recinto: t.recinto,
                        ingresos: t.ingresosTotal,
                        inscripciones: t.totalInscripciones,
                        ocupacion: t.tasaOcupacion,
                        ticket: t.ingresoPromedio,
                    })),
                    notas: [`Ranking por ${criterioTop}, top ${limiteTop}.`],
                });
            }

            if (generales?.ventasPorCategoria?.length) {
                hojas.push({
                    nombre: 'Por categoría',
                    columnas: [
                        { header: 'Categoría', key: 'categoria', tipo: 'texto', ancho: 28 },
                        { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
                        { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
                        { header: 'Ticket promedio', key: 'ticket', tipo: 'moneda' },
                    ],
                    filas: generales.ventasPorCategoria.map(c => ({
                        categoria: c.categoria,
                        ingresos: c.ingresos,
                        inscripciones: c.inscripciones,
                        ticket: c.inscripciones ? Math.round(c.ingresos / c.inscripciones) : 0,
                    })),
                });
            }

            if (generales?.ventasPorPlan?.length) {
                hojas.push({
                    nombre: 'Por plan',
                    columnas: [
                        { header: 'Plan', key: 'plan', tipo: 'texto', ancho: 26 },
                        { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
                        { header: 'Inscripciones', key: 'inscripciones', tipo: 'numero', total: true },
                        { header: 'Ticket promedio', key: 'ticket', tipo: 'moneda' },
                    ],
                    filas: generales.ventasPorPlan.map(p => ({
                        plan: p.plan,
                        ingresos: p.ingresos,
                        inscripciones: p.inscripciones,
                        ticket: p.inscripciones ? Math.round(p.ingresos / p.inscripciones) : 0,
                    })),
                });
            }

            // La matriz se veía solo como gráfico de dispersión: sin esta hoja el
            // Excel perdía la clasificación estrella / potencial / popular / en riesgo.
            if (matrizRentabilidad?.matriz) {
                const m = matrizRentabilidad.matriz;
                const conCuadrante = [
                    ...(m.estrellas || []).map(t => ({ ...t, cuadrante: 'Estrella' })),
                    ...(m.potenciales || []).map(t => ({ ...t, cuadrante: 'Potencial' })),
                    ...(m.populares || []).map(t => ({ ...t, cuadrante: 'Popular' })),
                    ...(m.enRiesgo || []).map(t => ({ ...t, cuadrante: 'En riesgo' })),
                ];
                if (conCuadrante.length) {
                    hojas.push({
                        nombre: 'Matriz de rentabilidad',
                        columnas: [
                            { header: 'Taller', key: 'nombre', tipo: 'texto', ancho: 38 },
                            {
                                header: 'Cuadrante', key: 'cuadrante', tipo: 'texto', ancho: 14,
                                resaltar: (t) => (t.cuadrante === 'Estrella' ? 'ok' : t.cuadrante === 'En riesgo' ? 'alerta' : 'aviso'),
                            },
                            { header: '% ocupación', key: 'ocupacion', tipo: 'porcentaje' },
                            { header: 'Ingresos', key: 'ingresos', tipo: 'moneda', total: true },
                        ],
                        filas: conCuadrante.map(t => ({
                            nombre: t.nombre,
                            cuadrante: t.cuadrante,
                            ocupacion: t.tasaOcupacion,
                            ingresos: t.ingresosTotal,
                        })),
                        notas: ['Cuadrantes cruzando ocupación e ingresos, igual que el gráfico de dispersión del panel.'],
                    });
                }
            }

            await exportarLibro({
                archivo: 'ventas-por-taller',
                titulo: 'Ventas por taller',
                subtitulo: `Reporte de ventas · ${fechas[0].format('DD/MM/YYYY')} al ${fechas[1].format('DD/MM/YYYY')}`,
                usuario: user,
                filtros: {
                    'Desde': fechas[0].format('DD/MM/YYYY'),
                    'Hasta': fechas[1].format('DD/MM/YYYY'),
                    'Criterio del ranking': criterioTop,
                    'Talleres en el top': limiteTop,
                },
                hojas,
            });
            message.success('Excel exportado correctamente');
        } catch (error) {
            console.error('Error exportando Excel:', error);
            message.error('Error al generar el Excel');
        }
    };

    // Configuración de gráficos
    const getLineChartData = () => {
        if (!evolucion) return null;

        return {
            labels: evolucion.evolucion.map(e => e.periodoNombre),
            datasets: [
                {
                    label: 'Ingresos',
                    data: evolucion.evolucion.map(e => e.ingresos),
                    borderColor: '#0047bb',
                    backgroundColor: 'rgba(0, 71, 187, 0.1)',
                    fill: true,
                    tension: 0.4,
                    yAxisID: 'y'
                },
                {
                    label: 'Inscripciones',
                    data: evolucion.evolucion.map(e => e.inscripciones),
                    borderColor: '#0047bb',
                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                    fill: true,
                    tension: 0.4,
                    yAxisID: 'y1'
                }
            ]
        };
    };

    const getPieChartDataCategorias = () => {
        if (!estadisticasGenerales) return null;

        return {
            labels: estadisticasGenerales.ventasPorCategoria.map(c => c.categoria),
            datasets: [
                {
                    data: estadisticasGenerales.ventasPorCategoria.map(c => c.ingresos),
                    backgroundColor: [
                        'rgba(255, 99, 132, 0.8)',
                        'rgba(54, 162, 235, 0.8)',
                        'rgba(255, 206, 86, 0.8)',
                        'rgba(75, 192, 192, 0.8)',
                        'rgba(153, 102, 255, 0.8)',
                        'rgba(255, 159, 64, 0.8)'
                    ],
                    borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(153, 102, 255, 1)',
                        'rgba(255, 159, 64, 1)'
                    ],
                    borderWidth: 2
                }
            ]
        };
    };

    const getBarChartDataPlanes = () => {
        if (!estadisticasGenerales) return null;

        return {
            labels: estadisticasGenerales.ventasPorPlan.map(p => p.plan),
            datasets: [
                {
                    label: 'Ingresos',
                    data: estadisticasGenerales.ventasPorPlan.map(p => p.ingresos),
                    backgroundColor: 'rgba(0, 71, 187, 0.6)',
                    borderColor: 'rgba(0, 71, 187, 1)',
                    borderWidth: 1
                }
            ]
        };
    };

    const getScatterData = () => {
        if (!matrizRentabilidad) return null;

        const allTalleres = [
            ...matrizRentabilidad.matriz.estrellas,
            ...matrizRentabilidad.matriz.potenciales,
            ...matrizRentabilidad.matriz.populares,
            ...matrizRentabilidad.matriz.enRiesgo
        ];

        return {
            datasets: [
                {
                    label: 'Estrellas',
                    data: matrizRentabilidad.matriz.estrellas.map(t => ({
                        x: t.tasaOcupacion,
                        y: t.ingresosTotal,
                        nombre: t.nombre
                    })),
                    backgroundColor: 'rgba(0, 71, 187, 0.8)',
                    pointRadius: 8
                },
                {
                    label: 'Potenciales',
                    data: matrizRentabilidad.matriz.potenciales.map(t => ({
                        x: t.tasaOcupacion,
                        y: t.ingresosTotal,
                        nombre: t.nombre
                    })),
                    backgroundColor: 'rgba(250, 173, 20, 0.8)',
                    pointRadius: 8
                },
                {
                    label: 'Populares',
                    data: matrizRentabilidad.matriz.populares.map(t => ({
                        x: t.tasaOcupacion,
                        y: t.ingresosTotal,
                        nombre: t.nombre
                    })),
                    backgroundColor: 'rgba(24, 144, 255, 0.8)',
                    pointRadius: 8
                },
                {
                    label: 'En Riesgo',
                    data: matrizRentabilidad.matriz.enRiesgo.map(t => ({
                        x: t.tasaOcupacion,
                        y: t.ingresosTotal,
                        nombre: t.nombre
                    })),
                    backgroundColor: 'rgba(255, 77, 79, 0.8)',
                    pointRadius: 8
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            if (context.dataset.label === 'Ingresos') {
                                label += formatearMoneda(context.parsed.y);
                            } else {
                                label += context.parsed.y;
                            }
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Ingresos ($)'
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Inscripciones'
                },
                grid: {
                    drawOnChartArea: false,
                }
            }
        }
    };

    const scatterOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const punto = context.raw;
                        return [
                            `Taller: ${punto.nombre}`,
                            `Ocupación: ${punto.x}%`,
                            `Ingresos: ${formatearMoneda(punto.y)}`
                        ];
                    }
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Tasa de Ocupación (%)'
                },
                min: 0,
                max: 100
            },
            y: {
                title: {
                    display: true,
                    text: 'Ingresos Totales ($)'
                },
                beginAtZero: true
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = formatearMoneda(context.parsed);
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        }
    };

    const renderTabGeneral = () => (
        <div>
            {/* KPIs Principales */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} lg={6}>
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <Statistic
                            title={
                                <span className="text-gray-600 font-medium">
                                    <DollarOutlined className="mr-2" />
                                    Ingresos Totales
                                </span>
                            }
                            value={estadisticasGenerales.resumen.ingresosTotal}
                            prefix="$"
                            valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                        />
                        <Divider className="my-2" />
                        <Text type="secondary" className="text-xs">
                            Brutos: {formatearMoneda(estadisticasGenerales.resumen.ingresosBrutos)}
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <Statistic
                            title={
                                <span className="text-gray-600 font-medium">
                                    <ShoppingCartOutlined className="mr-2" />
                                    Total Inscripciones
                                </span>
                            }
                            value={estadisticasGenerales.resumen.totalInscripciones}
                            valueStyle={{ color: '#0047bb', fontSize: '32px', fontWeight: 'bold' }}
                        />
                        <Divider className="my-2" />
                        <Text type="secondary" className="text-xs">
                            Usuarios únicos: {estadisticasGenerales.resumen.usuariosUnicos}
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <Statistic
                            title={
                                <span className="text-gray-600 font-medium">
                                    <DollarOutlined className="mr-2" />
                                    Ticket Promedio
                                </span>
                            }
                            value={estadisticasGenerales.resumen.ticketPromedio}
                            prefix="$"
                            precision={0}
                            valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
                        />
                        <Divider className="my-2" />
                        <Text type="secondary" className="text-xs">
                            Por inscripción
                        </Text>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <Statistic
                            title={
                                <span className="text-gray-600 font-medium">
                                    <PercentageOutlined className="mr-2" />
                                    Ocupación Global
                                </span>
                            }
                            value={estadisticasGenerales.resumen.ocupacionGlobal}
                            suffix="%"
                            precision={2}
                            valueStyle={{ 
                                color: estadisticasGenerales.resumen.ocupacionGlobal >= 80 ? '#0047bb' :
                                       estadisticasGenerales.resumen.ocupacionGlobal >= 50 ? '#faad14' : '#ff4d4f',
                                fontSize: '32px', 
                                fontWeight: 'bold' 
                            }}
                        />
                        <Divider className="my-2" />
                        <Progress 
                            percent={estadisticasGenerales.resumen.ocupacionGlobal} 
                            strokeColor={
                                estadisticasGenerales.resumen.ocupacionGlobal >= 80 ? '#0047bb' :
                                estadisticasGenerales.resumen.ocupacionGlobal >= 50 ? '#faad14' : '#ff4d4f'
                            }
                            showInfo={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* KPIs Secundarios */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={8}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Talleres Activos"
                            value={estadisticasGenerales.resumen.talleresActivos}
                            valueStyle={{ color: '#0047bb' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Talleres con Ventas"
                            value={estadisticasGenerales.resumen.talleresConVentas}
                            valueStyle={{ color: '#0047bb' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Descuentos Aplicados"
                            value={estadisticasGenerales.resumen.descuentosTotal}
                            prefix="$"
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Gráficos */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} lg={16}>
                    <Card 
                        title={
                            <span className="text-lg font-semibold">
                                <LineChartOutlined className="mr-2 text-blue-500" />
                                Evolución de Ventas ({mesesEvolucion} meses)
                            </span>
                        }
                        className="shadow-sm"
                        style={{ height: '450px' }}
                    >
                        {evolucion && getLineChartData() && (
                            <div style={{ height: '350px' }}>
                                <Line data={getLineChartData()} options={chartOptions} />
                            </div>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card 
                        title={
                            <span className="text-lg font-semibold">
                                <PieChartOutlined className="mr-2 text-purple-500" />
                                Ventas por Categoría
                            </span>
                        }
                        className="shadow-sm"
                        style={{ height: '450px' }}
                    >
                        {getPieChartDataCategorias() && (
                            <div style={{ height: '350px' }}>
                                <Pie data={getPieChartDataCategorias()} options={pieOptions} />
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Tablas */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <span className="text-lg font-semibold">
                                <PieChartOutlined className="mr-2 text-blue-500" />
                                Ventas por Categoría
                            </span>
                        }
                        className="shadow-sm"
                    >
                        <Table
                            dataSource={estadisticasGenerales.ventasPorCategoria}
                            rowKey="categoria"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Categoría',
                                    dataIndex: 'categoria',
                                    key: 'categoria',
                                    render: (val) => <Text strong>{val}</Text>
                                },
                                {
                                    title: 'Inscripciones',
                                    dataIndex: 'inscripciones',
                                    key: 'inscripciones',
                                    align: 'center',
                                    render: (val) => <Badge count={val} style={{ backgroundColor: '#0047bb' }} />
                                },
                                {
                                    title: 'Ingresos',
                                    dataIndex: 'ingresos',
                                    key: 'ingresos',
                                    align: 'right',
                                    render: (val) => (
                                        <Text strong style={{ color: '#0047bb' }}>
                                            {formatearMoneda(val)}
                                        </Text>
                                    )
                                },
                                {
                                    title: '%',
                                    dataIndex: 'porcentajeIngresos',
                                    key: 'porcentaje',
                                    align: 'center',
                                    render: (val) => <Tag color="blue">{val}%</Tag>
                                }
                            ]}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card 
                        title={
                            <span className="text-lg font-semibold">
                                <BarChartOutlined className="mr-2 text-orange-500" />
                                Ventas por Tipo de Plan
                            </span>
                        }
                        className="shadow-sm"
                    >
                        <Table
                            dataSource={estadisticasGenerales.ventasPorPlan}
                            rowKey="plan"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Plan',
                                    dataIndex: 'plan',
                                    key: 'plan',
                                    render: (val) => <Text strong>{val}</Text>
                                },
                                {
                                    title: 'Cantidad',
                                    dataIndex: 'cantidad',
                                    key: 'cantidad',
                                    align: 'center',
                                    render: (val) => <Badge count={val} style={{ backgroundColor: '#722ed1' }} />
                                },
                                {
                                    title: 'Ingresos',
                                    dataIndex: 'ingresos',
                                    key: 'ingresos',
                                    align: 'right',
                                    render: (val) => (
                                        <Text strong style={{ color: '#0047bb' }}>
                                            {formatearMoneda(val)}
                                        </Text>
                                    )
                                },
                                {
                                    title: 'Ticket Prom.',
                                    dataIndex: 'ticketPromedio',
                                    key: 'ticketPromedio',
                                    align: 'right',
                                    render: (val) => formatearMoneda(val)
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );

    const renderTabTopTalleres = () => (
        <div>
            {/* Controles */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} md={8}>
                    <Space direction="vertical" className="w-full">
                        <Text strong>Ordenar por:</Text>
                        <Select
                            value={criterioTop}
                            onChange={setCriterioTop}
                            className="w-full"
                            size="large"
                        >
                            <Option value="ingresos">Ingresos</Option>
                            <Option value="inscripciones">Inscripciones</Option>
                            <Option value="ocupacion">Ocupación</Option>
                            <Option value="ticketPromedio">Ticket Promedio</Option>
                        </Select>
                    </Space>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Space direction="vertical" className="w-full">
                        <Text strong>Límite:</Text>
                        <Select
                            value={limiteTop}
                            onChange={setLimiteTop}
                            className="w-full"
                            size="large"
                        >
                            <Option value={5}>Top 5</Option>
                            <Option value={10}>Top 10</Option>
                            <Option value={20}>Top 20</Option>
                        </Select>
                    </Space>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={cargarDatos}
                        size="large"
                        className="mt-6 w-full"
                        style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
                    >
                        Actualizar
                    </Button>
                </Col>
            </Row>

            {/* Top Talleres Cards */}
            <Row gutter={[16, 16]}>
                {topTalleres && topTalleres.topTalleres.map((taller, index) => (
                    <Col xs={24} sm={12} lg={8} key={taller.tallerId}>
                        <Card
                            className="shadow-sm hover:shadow-lg transition-all"
                            style={{
                                borderLeft: `4px solid ${
                                    index === 0 ? '#faad14' :
                                    index === 1 ? '#d9d9d9' :
                                    index === 2 ? '#cd7f32' : '#0047bb'
                                }`
                            }}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                    <Badge
                                        count={index + 1}
                                        style={{
                                            backgroundColor: index === 0 ? '#faad14' :
                                                           index === 1 ? '#d9d9d9' :
                                                           index === 2 ? '#cd7f32' : '#0047bb'
                                        }}
                                        className="mb-2"
                                    />
                                    <AntTitle level={5} className="mb-1">{taller.nombre}</AntTitle>
                                    <Text type="secondary" className="text-xs block mb-1">
                                        {taller.categoria} • {taller.recinto}
                                    </Text>
                                    {taller.instructor && (
                                        <Text type="secondary" className="text-xs block">
                                            Instructor: {taller.instructor}
                                        </Text>
                                    )}
                                </div>
                            </div>

                            <Divider className="my-3" />

                            <Row gutter={[8, 8]}>
                                <Col span={12}>
                                    <Text type="secondary" className="text-xs">Ingresos:</Text>
                                    <div className="text-lg font-bold" style={{ color: '#0047bb' }}>
                                        {formatearMoneda(taller.ingresosTotal)}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" className="text-xs">Inscripciones:</Text>
                                    <div className="text-lg font-bold" style={{ color: '#0047bb' }}>
                                        {taller.totalInscripciones}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" className="text-xs">Ocupación:</Text>
                                    <Progress
                                        percent={taller.tasaOcupacion}
                                        strokeColor={
                                            taller.tasaOcupacion >= 80 ? '#0047bb' :
                                            taller.tasaOcupacion >= 50 ? '#faad14' : '#ff4d4f'
                                        }
                                        size="small"
                                    />
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" className="text-xs">Ticket Prom:</Text>
                                    <div className="text-sm font-semibold">
                                        {formatearMoneda(taller.ingresoPromedio)}
                                    </div>
                                </Col>
                            </Row>

                            <Divider className="my-3" />

                            <Space size="small" className="w-full" direction="horizontal">
                                <Tooltip title="Ver detalles">
                                    <Button
                                        type="link"
                                        icon={<EyeOutlined />}
                                        size="small"
                                    >
                                        Detalles
                                    </Button>
                                </Tooltip>
                                <Checkbox
                                    checked={talleresSeleccionados.includes(taller.tallerId)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setTalleresSeleccionados([...talleresSeleccionados, taller.tallerId]);
                                        } else {
                                            setTalleresSeleccionados(talleresSeleccionados.filter(id => id !== taller.tallerId));
                                        }
                                    }}
                                >
                                    <Text className="text-xs">Comparar</Text>
                                </Checkbox>
                            </Space>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Botón de comparación */}
            {talleresSeleccionados.length >= 2 && (
                <Row className="mt-6">
                    <Col span={24} className="text-center">
                        <Button
                            type="primary"
                            size="large"
                            icon={<LuGitCompare />}
                            onClick={handleComparar}
                            style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                        >
                            Comparar {talleresSeleccionados.length} Talleres Seleccionados
                        </Button>
                    </Col>
                </Row>
            )}

            {/* Resultados de comparación */}
            {comparacion && (
                <Row gutter={[16, 16]} className="mt-6">
                    <Col span={24}>
                        <Card
                            title={
                                <span className="text-lg font-semibold">
                                    <LuGitCompare className="mr-2" />
                                    Comparación de Talleres
                                </span>
                            }
                            className="shadow-sm"
                        >
                            <Table
                                dataSource={comparacion.comparacion}
                                rowKey="tallerId"
                                pagination={false}
                                scroll={{ x: 1200 }}
                                columns={[
                                    {
                                        title: 'Taller',
                                        dataIndex: 'nombre',
                                        key: 'nombre',
                                        fixed: 'left',
                                        width: 200,
                                        render: (val, record) => (
                                            <div>
                                                <Text strong className="block">{val}</Text>
                                                <Text type="secondary" className="text-xs">
                                                    {record.categoria}
                                                </Text>
                                            </div>
                                        )
                                    },
                                    {
                                        title: 'Ingresos',
                                        dataIndex: 'ingresosTotal',
                                        key: 'ingresos',
                                        align: 'right',
                                        sorter: (a, b) => a.ingresosTotal - b.ingresosTotal,
                                        render: (val) => (
                                            <Text strong style={{ color: '#0047bb' }}>
                                                {formatearMoneda(val)}
                                            </Text>
                                        )
                                    },
                                    {
                                        title: 'Inscripciones',
                                        dataIndex: 'totalInscripciones',
                                        key: 'inscripciones',
                                        align: 'center',
                                        sorter: (a, b) => a.totalInscripciones - b.totalInscripciones,
                                        render: (val) => <Badge count={val} style={{ backgroundColor: '#0047bb' }} />
                                    },
                                    {
                                        title: 'Ocupación',
                                        dataIndex: 'tasaOcupacion',
                                        key: 'ocupacion',
                                        align: 'center',
                                        width: 150,
                                        sorter: (a, b) => a.tasaOcupacion - b.tasaOcupacion,
                                        render: (val) => (
                                            <Progress
                                                percent={val}
                                                strokeColor={
                                                    val >= 80 ? '#0047bb' :
                                                    val >= 50 ? '#faad14' : '#ff4d4f'
                                                }
                                                size="small"
                                            />
                                        )
                                    },
                                    {
                                        title: 'Ticket Prom.',
                                        dataIndex: 'ingresoPromedio',
                                        key: 'ticketPromedio',
                                        align: 'right',
                                        sorter: (a, b) => a.ingresoPromedio - b.ingresoPromedio,
                                        render: (val) => formatearMoneda(val)
                                    },
                                    {
                                        title: 'Usuarios Únicos',
                                        dataIndex: 'usuariosUnicos',
                                        key: 'usuarios',
                                        align: 'center',
                                        sorter: (a, b) => a.usuariosUnicos - b.usuariosUnicos
                                    }
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );

    const renderTabMatriz = () => (
        <div>
            {/* Explicación */}
            <Alert
                message="Matriz de Rentabilidad"
                description="Los talleres se clasifican según su ocupación e ingresos. Estrellas son los mejores, En Riesgo requieren atención."
                type="info"
                showIcon
                className="mb-6"
            />

            {/* Criterios */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Mediana de Ingresos"
                            value={matrizRentabilidad.criterios.medianaIngresos}
                            prefix="$"
                            valueStyle={{ color: '#0047bb' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Mediana de Ocupación"
                            value={matrizRentabilidad.criterios.medianaOcupacion}
                            suffix="%"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Resumen */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                    <Card className="shadow-sm" style={{ borderLeft: '4px solid #0047bb' }}>
                        <Statistic
                            title={
                                <span>
                                    <StarOutlined className="mr-2" style={{ color: '#0047bb' }} />
                                    Estrellas
                                </span>
                            }
                            value={matrizRentabilidad.resumen.estrellas}
                            valueStyle={{ color: '#0047bb' }}
                        />
                        <Text type="secondary" className="text-xs">Alta ocupación, altos ingresos</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card className="shadow-sm" style={{ borderLeft: '4px solid #faad14' }}>
                        <Statistic
                            title={
                                <span>
                                    <ThunderboltOutlined className="mr-2" style={{ color: '#faad14' }} />
                                    Potenciales
                                </span>
                            }
                            value={matrizRentabilidad.resumen.potenciales}
                            valueStyle={{ color: '#faad14' }}
                        />
                        <Text type="secondary" className="text-xs">Baja ocupación, altos ingresos</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card className="shadow-sm" style={{ borderLeft: '4px solid #0047bb' }}>
                        <Statistic
                            title={
                                <span>
                                    <FireOutlined className="mr-2" style={{ color: '#0047bb' }} />
                                    Populares
                                </span>
                            }
                            value={matrizRentabilidad.resumen.populares}
                            valueStyle={{ color: '#0047bb' }}
                        />
                        <Text type="secondary" className="text-xs">Alta ocupación, bajos ingresos</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card className="shadow-sm" style={{ borderLeft: '4px solid #ff4d4f' }}>
                        <Statistic
                            title={
                                <span>
                                    <WarningOutlined className="mr-2" style={{ color: '#ff4d4f' }} />
                                    En Riesgo
                                </span>
                            }
                            value={matrizRentabilidad.resumen.enRiesgo}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                        <Text type="secondary" className="text-xs">Baja ocupación, bajos ingresos</Text>
                    </Card>
                </Col>
            </Row>

            {/* Gráfico de dispersión */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col span={24}>
                    <Card
                        title={
                            <span className="text-lg font-semibold">
                                <BarChartOutlined className="mr-2 text-purple-500" />
                                Matriz de Rentabilidad
                            </span>
                        }
                        className="shadow-sm"
                        style={{ height: '600px' }}
                    >
                        {getScatterData() && (
                            <div style={{ height: '500px' }}>
                                <Scatter data={getScatterData()} options={scatterOptions} />
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Listas por categoría */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span className="text-md font-semibold">
                                <StarOutlined className="mr-2" style={{ color: '#0047bb' }} />
                                Talleres Estrella ({matrizRentabilidad.matriz.estrellas.length})
                            </span>
                        }
                        className="shadow-sm"
                        style={{ borderTop: '3px solid #0047bb' }}
                    >
                        <List
                            dataSource={matrizRentabilidad.matriz.estrellas}
                            renderItem={(taller) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={taller.nombre}
                                        description={
                                            <div>
                                                <Text className="text-xs block">Categoría: {taller.categoria}</Text>
                                                <Space size="small" className="mt-1">
                                                    <Tag color="blue">{formatearMoneda(taller.ingresosTotal)}</Tag>
                                                    <Tag color="blue">{taller.tasaOcupacion}% ocupación</Tag>
                                                </Space>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        title={
                            <span className="text-md font-semibold">
                                <WarningOutlined className="mr-2" style={{ color: '#ff4d4f' }} />
                                Talleres en Riesgo ({matrizRentabilidad.matriz.enRiesgo.length})
                            </span>
                        }
                        className="shadow-sm"
                        style={{ borderTop: '3px solid #ff4d4f' }}
                    >
                        <List
                            dataSource={matrizRentabilidad.matriz.enRiesgo}
                            renderItem={(taller) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={taller.nombre}
                                        description={
                                            <div>
                                                <Text className="text-xs block">Categoría: {taller.categoria}</Text>
                                                <Space size="small" className="mt-1">
                                                    <Tag color="red">{formatearMoneda(taller.ingresosTotal)}</Tag>
                                                    <Tag color="orange">{taller.tasaOcupacion}% ocupación</Tag>
                                                </Space>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <AntTitle level={2} className="mb-2">
                    <TrophyOutlined className="mr-2" />
                    Ventas por Taller
                </AntTitle>
                <Text type="secondary">
                    Seguimiento y análisis completo de ventas e ingresos por taller
                </Text>
            </div>

            {/* Filtros */}
            <Card className="mb-6 shadow-sm">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={12}>
                        <Space direction="vertical" className="w-full">
                            <Text strong>
                                <CalendarOutlined className="mr-2" />
                                Periodo de Análisis:
                            </Text>
                            <RangePicker
                                value={fechas}
                                onChange={setFechas}
                                format="DD/MM/YYYY"
                                className="w-full"
                                size="large"
                            />
                        </Space>
                    </Col>

                    <Col xs={24} md={6}>
                        <Space direction="vertical" className="w-full">
                            <Text strong>Meses Evolución:</Text>
                            <Select
                                value={mesesEvolucion}
                                onChange={setMesesEvolucion}
                                className="w-full"
                                size="large"
                            >
                                <Option value={3}>3 meses</Option>
                                <Option value={6}>6 meses</Option>
                                <Option value={12}>12 meses</Option>
                            </Select>
                        </Space>
                    </Col>

                    <Col xs={24} md={6}>
                        <Space className="w-full mt-6">
                            <Button
                                type="primary"
                                size="large"
                                icon={<ReloadOutlined />}
                                onClick={cargarDatos}
                                loading={loading}
                                style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
                            >
                                Actualizar
                            </Button>
                            <Tooltip title="Exportar reporte (Top Talleres + Por Categoría + Por Plan)">
                                <Button
                                    size="large"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportarExcel}
                                    disabled={!topTalleres && !estadisticasGenerales}
                                >
                                    Exportar Excel
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Spin size="large" tip="Cargando datos de ventas..." />
                </div>
            ) : estadisticasGenerales ? (
                <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
                    <TabPane
                        tab={
                            <span>
                                <BarChartOutlined />
                                General
                            </span>
                        }
                        key="general"
                    >
                        {renderTabGeneral()}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <TrophyOutlined />
                                Top Talleres
                            </span>
                        }
                        key="top"
                    >
                        {renderTabTopTalleres()}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <StarOutlined />
                                Matriz
                            </span>
                        }
                        key="matriz"
                    >
                        {renderTabMatriz()}
                    </TabPane>
                </Tabs>
            ) : (
                <Empty description="No hay datos disponibles" />
            )}
        </div>
    );
};

export default VentasTalleres;
